import React, { useState, useEffect, useCallback, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus,
  faEdit,
  faTrash,
  faCopy,
  faToggleOn,
  faToggleOff,
  faLink,
  faUnlink,
  faSatelliteDish,
  faEye,
  faEyeSlash,
  faSearch
} from '@fortawesome/free-solid-svg-icons';
import { api, getApiUrl } from '../../utils/apiConfig';

function ChannelManagement({ userId, refreshTrigger, onRefresh }) {
  const [channels, setChannels] = useState([]);
  const [channelTypes, setChannelTypes] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [accountChannels, setAccountChannels] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Modal states
  const [showChannelModal, setShowChannelModal] = useState(false);
  const [showAccountChannelModal, setShowAccountChannelModal] = useState(false);
  const [editingChannel, setEditingChannel] = useState(null);
  const [selectedChannelType, setSelectedChannelType] = useState('');
  const [channelFormData, setChannelFormData] = useState({
    name: '',
    channel_type_id: '',
    field_values: {}
  });
  const [accountChannelFormData, setAccountChannelFormData] = useState({
    account_id: '',
    channel_id: '',
    is_enabled: true
  });

  // State for tracking which sensitive fields are visible
  const [visibleSensitiveFields, setVisibleSensitiveFields] = useState({});

  // Modal error state (for displaying errors inside the modal)
  const [modalError, setModalError] = useState(null);

  // Sorting
  const [sortConfig, setSortConfig] = useState({
    key: 'name',
    direction: 'ascending'
  });

  // Name filter for the Channel Configs table
  const [nameFilter, setNameFilter] = useState('');
  const [nameFilterInputValue, setNameFilterInputValue] = useState(''); // Track input value separately
  const nameFilterDebounceRef = useRef(null);

  // Add a ref to track if we're currently fetching data
  const isFetchingDataRef = useRef(false);

  // Helper function to identify sensitive fields
  const isSensitiveField = (fieldName) => {
    const sensitiveKeywords = [
      'password',
      'secret',
      'token',
      'key',
      'service_account',
      'refresh_token',
      'access_token',
      'client_secret',
      'api_key',
      'private_key',
      'auth_token',
      'yaml_string'
    ];

    const lowerFieldName = fieldName.toLowerCase();
    return sensitiveKeywords.some(keyword => lowerFieldName.includes(keyword));
  };

  // Function to toggle visibility of sensitive fields
  const toggleSensitiveFieldVisibility = (fieldName) => {
    setVisibleSensitiveFields(prev => ({
      ...prev,
      [fieldName]: !prev[fieldName]
    }));
  };

  // Memoize the fetchData function to prevent unnecessary re-creation
  const fetchData = useCallback(async () => {
    // If we're already fetching data, don't start another fetch
    if (isFetchingDataRef.current) {
      return;
    }

    try {
      // Set the fetching flag to prevent duplicate API calls
      isFetchingDataRef.current = true;
      setIsLoading(true);

      // Fetch all required data in parallel
      const [channelsRes, channelTypesRes, accountsRes, accountChannelsRes] = await Promise.all([
        api.get(getApiUrl('/api/channels'), {
          params: { userId }
        }),
        api.get(getApiUrl('/api/channels/types'), {
          params: { userId }
        }),
        api.get(getApiUrl('/api/admin/accounts'), {
          params: { userId }
        }),
        api.get(getApiUrl('/api/channels/accounts'), {
          params: { userId }
        })
      ]);

      setChannels(channelsRes.data);
      setChannelTypes(channelTypesRes.data);
      setAccounts(accountsRes.data);
      setAccountChannels(accountChannelsRes.data);
      setIsLoading(false);
    } catch (error) {
      console.error('Error fetching channel data:', error);
      setError('Failed to load channel data. Please try again later.');
      setIsLoading(false);
    } finally {
      // Reset the fetching flag
      isFetchingDataRef.current = false;
    }
  }, [userId]);

  // Fetch data when the component mounts or refreshTrigger changes
  useEffect(() => {
    // Use a small timeout to debounce multiple rapid calls
    const debounceTimeout = setTimeout(() => {
      fetchData();
    }, 50);

    // Clean up the timeout on unmount or when dependencies change
    return () => {
      clearTimeout(debounceTimeout);
    };
  }, [fetchData, refreshTrigger]);

  // Handle sorting when a column header is clicked
  const requestSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  // Get the sorted data
  const getSortedData = (data) => {
    if (!sortConfig.key) return data;

    return [...data].sort((a, b) => {
      // Handle null or undefined values
      if (a[sortConfig.key] == null) return sortConfig.direction === 'ascending' ? 1 : -1;
      if (b[sortConfig.key] == null) return sortConfig.direction === 'ascending' ? -1 : 1;

      // Handle date fields
      if (sortConfig.key === 'created_at' || sortConfig.key === 'updated_at') {
        const dateA = a[sortConfig.key] ? new Date(a[sortConfig.key]) : new Date(0);
        const dateB = b[sortConfig.key] ? new Date(b[sortConfig.key]) : new Date(0);
        return sortConfig.direction === 'ascending' ? dateA - dateB : dateB - dateA;
      }

      // Handle string fields
      if (typeof a[sortConfig.key] === 'string' && typeof b[sortConfig.key] === 'string') {
        return sortConfig.direction === 'ascending'
          ? a[sortConfig.key].localeCompare(b[sortConfig.key])
          : b[sortConfig.key].localeCompare(a[sortConfig.key]);
      }

      // Handle other fields
      return sortConfig.direction === 'ascending'
        ? a[sortConfig.key] > b[sortConfig.key] ? 1 : -1
        : a[sortConfig.key] < b[sortConfig.key] ? 1 : -1;
    });
  };

  // Get sort direction indicator
  const getSortDirectionIndicator = (key) => {
    if (sortConfig.key !== key) return null;
    return sortConfig.direction === 'ascending' ? ' ↑' : ' ↓';
  };

  // Create a debounced filter function to prevent rapid consecutive renders
  const handleNameFilterChange = useCallback((e) => {
    setNameFilterInputValue(e.target.value);

    if (nameFilterDebounceRef.current) {
      clearTimeout(nameFilterDebounceRef.current);
    }

    nameFilterDebounceRef.current = setTimeout(() => {
      setNameFilter(e.target.value);
    }, 300);
  }, []);

  // Clean up the filter debounce timeout when the component unmounts
  useEffect(() => {
    return () => {
      if (nameFilterDebounceRef.current) {
        clearTimeout(nameFilterDebounceRef.current);
      }
    };
  }, []);

  // Apply name filter to channels
  const filteredChannels = nameFilter.trim()
    ? channels.filter(channel =>
        (channel.name || '').toLowerCase().includes(nameFilter.trim().toLowerCase())
      )
    : channels;

  // Apply sorting to channels
  const sortedChannels = getSortedData(filteredChannels);

  // Apply name filter to account channel assignments (by channel name)
  const filteredAccountChannels = nameFilter.trim()
    ? accountChannels.filter(ac =>
        (ac.channel_name || '').toLowerCase().includes(nameFilter.trim().toLowerCase())
      )
    : accountChannels;

  // Apply sorting to account channels
  const sortedAccountChannels = getSortedData(filteredAccountChannels);

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString();
  };

  const handleAddChannel = () => {
    setEditingChannel(null);
    setChannelFormData({
      name: '',
      channel_type_id: '',
      field_values: {}
    });
    setSelectedChannelType('');
    setVisibleSensitiveFields({}); // Reset sensitive field visibility
    setModalError(null); // Clear any previous modal errors
    setShowChannelModal(true);
  };

  const handleEditChannel = (channel) => {
    setEditingChannel(channel);
    setChannelFormData({
      name: channel.name,
      channel_type_id: channel.channel_type_id,
      field_values: channel.field_values || {}
    });
    setSelectedChannelType(channel.channel_type_id);
    setVisibleSensitiveFields({}); // Reset sensitive field visibility
    setModalError(null); // Clear any previous modal errors
    setShowChannelModal(true);
  };

  const handleDuplicateChannel = (channel) => {
    // Open the create modal pre-filled with the source channel's data;
    // submitting creates a new channel since editingChannel stays null
    setEditingChannel(null);
    setChannelFormData({
      name: `${channel.name} (Copy)`,
      channel_type_id: channel.channel_type_id,
      field_values: { ...(channel.field_values || {}) }
    });
    setSelectedChannelType(channel.channel_type_id);
    setVisibleSensitiveFields({}); // Reset sensitive field visibility
    setModalError(null); // Clear any previous modal errors
    setShowChannelModal(true);
  };

  const handleDeleteChannel = async (channelId) => {
    if (!window.confirm('Are you sure you want to delete this channel? This action cannot be undone.')) {
      return;
    }

    try {
      await api.delete(`${getApiUrl(`/api/channels/${channelId}`)}`, {
        params: { userId }
      });

      // Update local state
      setChannels(channels.filter(channel => channel.id !== channelId));

      // Trigger refresh of all admin tables
      if (onRefresh) {
        onRefresh();
      }
    } catch (error) {
      console.error('Error deleting channel:', error);
      setError('Failed to delete channel: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleChannelTypeChange = (e) => {
    const channelTypeId = e.target.value;
    setSelectedChannelType(channelTypeId);

    // Keep values for fields the new channel type also has (matched by
    // field_name) so duplicating a config across platforms carries over
    // shared credentials; drop values that don't apply to the new type
    const newType = channelTypes.find(type => type.id === channelTypeId);
    const newFieldNames = new Set((newType?.fields || []).map(field => field.field_name));
    const preservedFieldValues = Object.fromEntries(
      Object.entries(channelFormData.field_values).filter(([fieldName]) => newFieldNames.has(fieldName))
    );

    setChannelFormData({
      ...channelFormData,
      channel_type_id: channelTypeId,
      field_values: preservedFieldValues
    });
    setVisibleSensitiveFields({}); // Reset sensitive field visibility
  };

  const handleChannelInputChange = (e) => {
    const { name, value } = e.target;
    const activeElement = document.activeElement;
    const activeElementId = activeElement ? activeElement.id : null;
    const selectionStart = activeElement && 'selectionStart' in activeElement ? activeElement.selectionStart : null;

    setChannelFormData(prevData => ({
      ...prevData,
      [name]: value
    }));

    // Restore focus and cursor position after state update
    setTimeout(() => {
      if (activeElementId) {
        const element = document.getElementById(activeElementId);
        if (element) {
          element.focus();
          if (selectionStart !== null && 'setSelectionRange' in element) {
            element.setSelectionRange(selectionStart, selectionStart);
          }
        }
      }
    }, 0);
  };

  const handleChannelFieldChange = (fieldName, value) => {
    const activeElement = document.activeElement;
    const activeElementId = activeElement ? activeElement.id : null;
    const selectionStart = activeElement && 'selectionStart' in activeElement ? activeElement.selectionStart : null;

    // For boolean fields, ensure the value is stored as a string 'true' or 'false'
    let processedValue = value;
    if (typeof value === 'boolean') {
      processedValue = value.toString();
    }

    setChannelFormData(prevData => ({
      ...prevData,
      field_values: {
        ...prevData.field_values,
        [fieldName]: processedValue
      }
    }));

    // Restore focus and cursor position after state update
    setTimeout(() => {
      if (activeElementId) {
        const element = document.getElementById(activeElementId);
        if (element) {
          element.focus();
          if (selectionStart !== null && 'setSelectionRange' in element) {
            element.setSelectionRange(selectionStart, selectionStart);
          }
        }
      }
    }, 0);
  };

  const handleChannelSubmit = async (e) => {
    e.preventDefault();

    try {
      // Get the selected channel type to ensure all required fields are included
      const selectedType = channelTypes.find(type => type.id === selectedChannelType);
      const formDataToSubmit = { ...channelFormData };

      // Ensure all boolean fields are included, even if not checked
      if (selectedType && selectedType.fields) {
        selectedType.fields.forEach(field => {
          if (field.field_type === 'boolean' && !formDataToSubmit.field_values[field.field_name]) {
            // If a boolean field is missing or falsy, explicitly set it to 'false'
            formDataToSubmit.field_values = {
              ...formDataToSubmit.field_values,
              [field.field_name]: 'false'
            };
          }
        });
      }

      let response;

      if (editingChannel) {
        // Update existing channel
        response = await api.put(
          getApiUrl(`/api/channels/${editingChannel.id}`),
          {
            ...formDataToSubmit,
            userId
          }
        );

        // Update local state
        setChannels(channels.map(channel =>
          channel.id === editingChannel.id ? response.data : channel
        ));
      } else {
        // Create new channel
        response = await api.post(
          getApiUrl('/api/channels'),
          {
            ...formDataToSubmit,
            userId
          }
        );

        // Update local state
        setChannels([...channels, response.data]);
      }

      // Close modal and reset form
      setShowChannelModal(false);
      setEditingChannel(null);
      setChannelFormData({
        name: '',
        channel_type_id: '',
        field_values: {}
      });

      // Trigger refresh of all admin tables
      if (onRefresh) {
        onRefresh();
      }
    } catch (error) {
      console.error('Error saving channel:', error);
      // Display error in the modal so user can see it while the modal is open
      setModalError('Failed to save channel: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleAddAccountChannel = () => {
    setAccountChannelFormData({
      account_id: '',
      channel_id: '',
      is_enabled: true
    });
    setShowAccountChannelModal(true);
  };

  const handleAccountChannelInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setAccountChannelFormData({
      ...accountChannelFormData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleAccountChannelSubmit = async (e) => {
    e.preventDefault();

    try {
      const response = await api.post(
        getApiUrl('/api/channels/accounts'),
        {
          ...accountChannelFormData,
          userId
        }
      );

      // Update local state
      setAccountChannels([...accountChannels, response.data]);

      // Close modal and reset form
      setShowAccountChannelModal(false);
      setAccountChannelFormData({
        account_id: '',
        channel_id: '',
        is_enabled: true
      });

      // Trigger refresh of all admin tables
      if (onRefresh) {
        onRefresh();
      }
    } catch (error) {
      console.error('Error creating account channel association:', error);
      setError('Failed to create association: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleToggleAccountChannel = async (accountChannelId, currentStatus) => {
    try {
      const response = await api.put(
        getApiUrl(`/api/channels/accounts/${accountChannelId}`),
        {
          is_enabled: !currentStatus,
          userId
        }
      );

      // Update local state
      setAccountChannels(accountChannels.map(ac =>
        ac.id === accountChannelId ? response.data : ac
      ));

      // Trigger refresh of all admin tables
      if (onRefresh) {
        onRefresh();
      }
    } catch (error) {
      console.error('Error toggling account channel:', error);
      setError('Failed to toggle channel access: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleRemoveAccountChannel = async (accountChannelId) => {
    if (!window.confirm('Are you sure you want to remove this channel from this account?')) {
      return;
    }

    try {
      await api.delete(getApiUrl(`/api/channels/accounts/${accountChannelId}`), {
        params: { userId }
      });

      // Update local state
      setAccountChannels(accountChannels.filter(ac => ac.id !== accountChannelId));

      // Trigger refresh of all admin tables
      if (onRefresh) {
        onRefresh();
      }
    } catch (error) {
      console.error('Error removing account channel:', error);
      setError('Failed to remove channel from account: ' + (error.response?.data?.error || error.message));
    }
  };

  // Every google-ads* channel type offers the OAuth option (the channel-type split
  // copied one field set across all of them). Mirrors GOOGLE_ADS_CHANNEL_TYPES in
  // the backend's services/googleAdsYamlConfig.js.
  const GOOGLE_ADS_CHANNEL_TYPES = [
    'google-ads',
    'google-ads-search',
    'google-ads-display',
    'google-ads-pmax',
    'google-ads-demandgen'
  ];
  const isGoogleAdsType = (selectedType) =>
    !!selectedType && GOOGLE_ADS_CHANNEL_TYPES.includes(
      String(selectedType.name || '').trim().toLowerCase().replace(/[\s_]+/g, '-')
    );
  const isOAuthChecked = () =>
    channelFormData.field_values['oauth'] === 'true' || channelFormData.field_values['oauth'] === true;

  // Helper function to sort fields for display
  // For Facebook and Google Ads: oauth first, then required fields, then optional fields
  const getSortedFields = (fields, selectedType) => {
    if (!fields || !selectedType) return fields;

    // For Facebook / Google Ads, sort: oauth first, then by required status
    if (selectedType.name === 'facebook' || isGoogleAdsType(selectedType)) {
      return [...fields].sort((a, b) => {
        // oauth always comes first
        if (a.field_name === 'oauth') return -1;
        if (b.field_name === 'oauth') return 1;

        // Determine effective required status considering oauth
        const aRequired = isFieldRequired(a, selectedType);
        const bRequired = isFieldRequired(b, selectedType);

        // Required fields come before optional fields
        if (aRequired && !bRequired) return -1;
        if (!aRequired && bRequired) return 1;

        // Within same required status, maintain alphabetical order
        return a.field_name.localeCompare(b.field_name);
      });
    }

    return fields;
  };

  // Helper function to determine if a field should be visible based on conditional logic
  const shouldShowField = (field, selectedType) => {
    // TTD channel conditional field visibility
    if (selectedType && selectedType.name === 'ttd') {
      const useToken = channelFormData.field_values['use_token'] === 'true';

      // If use_token is checked, hide user_id and password fields
      if (useToken && (field.field_name === 'user_id' || field.field_name === 'password')) {
        return false;
      }

      // If use_token is not checked, hide token field
      if (!useToken && field.field_name === 'token') {
        return false;
      }
    }

    // Facebook channel: all fields remain visible regardless of oauth status
    // (oauth only affects whether fields are required, not visibility)

    // Google Ads: show ONE mode's fields at a time — the form was showing all
    // of them at once ("Developer Token (OAuth)" next to an unchecked OAuth
    // toggle), leaving admins unable to tell which fields a given mode needs.
    //
    // OAuth ON : yaml_string is GENERATED by the OAuth callback from the
    //            refresh token — an editable box invites someone to paste a
    //            config the next scheduled token refresh silently overwrites.
    // OAuth OFF: developer_token / login_customer_id are consumed only by that
    //            same yaml synthesis (a hand-pasted yaml carries its own), so
    //            they are dead inputs — hidden, not just optional.
    if (isGoogleAdsType(selectedType)) {
      const useOAuth = isOAuthChecked();
      if (useOAuth && field.field_name === 'yaml_string') return false;
      if (!useOAuth && field.field_name === 'login_customer_id') return false;
      // Never offered: Google permanently pairs a Cloud project with ONE
      // developer token on the first API request. OAuth-mode channels ride the
      // platform's OAuth client, so the only token that can ever work is the
      // platform's (GOOGLE_ADS_DEVELOPER_TOKEN) — a per-channel override is a
      // credential Google would reject. Non-OAuth yamls carry their own.
      // (The DB field stays for rows that predate this rule.)
      if (field.field_name === 'developer_token') return false;
    }

    return true;
  };

  // Helper function to determine if a field is required based on conditional logic
  const isFieldRequired = (field, selectedType) => {
    // Facebook OAuth conditional requirements:
    // - When oauth IS checked: ad_account_id is optional, business_id is required
    // - When oauth is NOT checked: ad_account_id is required, business_id is optional
    // - page_id is always optional
    if (selectedType && selectedType.name === 'facebook') {
      const useOAuth = channelFormData.field_values['oauth'] === 'true' || channelFormData.field_values['oauth'] === true;

      if (field.field_name === 'ad_account_id') {
        // ad_account_id is required only when NOT using OAuth
        return !useOAuth;
      }
      if (field.field_name === 'business_id') {
        // business_id is required only when using OAuth
        return useOAuth;
      }
      // page_id is always optional
      if (field.field_name === 'page_id') {
        return false;
      }
    }

    // Google Ads conditional requirements (hidden fields are never required —
    // shouldShowField shows one mode's inputs at a time):
    // - oauth ON : yaml_string is generated (not required). The developer token
    //              is the platform's GOOGLE_ADS_DEVELOPER_TOKEN by necessity:
    //              Google pairs a Cloud project with one developer token
    //              permanently, and OAuth mode rides the platform's client.
    // - oauth OFF: yaml_string is required (a complete matched set — its own
    //              client_id/client_secret/refresh_token/developer_token).
    // - login_customer_id is always optional (only used for MCC-managed accounts)
    if (isGoogleAdsType(selectedType)) {
      const useOAuth = isOAuthChecked();

      if (field.field_name === 'yaml_string') {
        return !useOAuth;
      }
      if (field.field_name === 'developer_token' || field.field_name === 'login_customer_id') {
        return false;
      }
    }

    // TTD conditional requirements: if use_token is true, user_id and password are not required
    if (selectedType && selectedType.name === 'ttd') {
      const useToken = channelFormData.field_values['use_token'] === 'true';

      if (useToken && (field.field_name === 'user_id' || field.field_name === 'password')) {
        return false;
      }

      if (!useToken && field.field_name === 'token') {
        return false;
      }
    }

    // Default: use the field's is_required from database
    return field.is_required;
  };

  // Render channel field input based on field type
  const renderChannelFieldInput = (field, selectedType) => {
    const value = channelFormData.field_values[field.field_name] || '';
    const required = isFieldRequired(field, selectedType);

    switch (field.field_type) {
      case 'boolean':
        return (
          <div className="form-check">
            <input
              type="checkbox"
              id={field.field_name}
              className="form-check-input mr-2"
              checked={value === 'true' || value === true}
              onChange={(e) => handleChannelFieldChange(field.field_name, e.target.checked.toString())}

            />
            <label className="form-check-label" htmlFor={field.field_name}>
              {required ? `${field.field_label} *` : field.field_label}
            </label>
            {/* The oauth toggle flips which fields exist below it — say what
                each mode needs, right where the flip happens. */}
            {field.field_name === 'oauth' && isGoogleAdsType(selectedType) && (
              <div className="alert alert-info" style={{ marginTop: '8px', fontSize: '12px', padding: '10px 12px' }}>
                {isOAuthChecked() ? (
                  <>
                    <strong>OAuth mode:</strong> only <code>Customer ID</code> is required.
                    After saving, connect the channel from the OAuth admin page — the YAML
                    configuration is generated and kept current automatically, using the
                    platform&apos;s Google Ads app credentials and developer token.
                    Set <code>Login Customer ID</code> only when this customer is reached
                    through an MCC.
                  </>
                ) : (
                  <>
                    <strong>Manual mode:</strong> <code>Customer ID</code> and a complete{' '}
                    <code>YAML Configuration</code> are required. The YAML must be a matched
                    set — the <code>refresh_token</code> only works with the{' '}
                    <code>client_id</code>/<code>client_secret</code> that issued it, and the{' '}
                    <code>developer_token</code> must be the one paired with that client&apos;s
                    Google Cloud project.
                  </>
                )}
              </div>
            )}
          </div>
        );
      case 'number':
        return (
          <div className="form-group">
            <label htmlFor={field.field_name}>
              {required ? `${field.field_label} *` : field.field_label}
            </label>
            <input
              type="number"
              id={field.field_name}
              className="form-control"
              value={value}
              onChange={(e) => handleChannelFieldChange(field.field_name, e.target.value)}
              required={required}

            />
          </div>
        );
      case 'textarea':
        const isSensitiveTextarea = isSensitiveField(field.field_name);
        const isVisibleTextarea = visibleSensitiveFields[field.field_name] || false;

        return (
          <div className="form-group">
            <label htmlFor={field.field_name}>
              {required ? `${field.field_label} *` : field.field_label}
            </label>
            <div className={`input-group ${isSensitiveTextarea ? 'sensitive-field' : ''}`}>
              {/* Always a textarea, masked by CSS while hidden: a single-line
                  password input drops the line breaks of a pasted PEM or YAML. */}
              <textarea
                id={field.field_name}
                className="form-control"
                value={value}
                onChange={(e) => handleChannelFieldChange(field.field_name, e.target.value)}
                required={required}
                rows={isSensitiveTextarea && !isVisibleTextarea ? 3 : 6}
                autoComplete="off"
                spellCheck={false}
                style={{
                  fontFamily: 'monospace',
                  fontSize: '12px',
                  ...(isSensitiveTextarea && !isVisibleTextarea ? { WebkitTextSecurity: 'disc' } : {}),
                }}
              />
              {isSensitiveTextarea && (
                <div className="input-group-append">
                  <button
                    type="button"
                    className="btn btn-outline-secondary toggle-visibility-btn"
                    onClick={() => toggleSensitiveFieldVisibility(field.field_name)}
                    title={isVisibleTextarea ? "Hide" : "Show"}
                  >
                    <FontAwesomeIcon icon={isVisibleTextarea ? faEyeSlash : faEye} />
                  </button>
                </div>
              )}
            </div>
            {field.field_name === 'yaml_string' && (
              <div className="alert alert-info" style={{ marginTop: '8px', fontSize: '12px', padding: '10px 12px' }}>
                <strong>Configuration Tips:</strong>
                <ul style={{ margin: '6px 0 0 0', paddingLeft: '18px' }}>
                  <li>For MCC accounts, <code>customer_id</code> and <code>login_customer_id</code> should be the same</li>
                  <li>When using service account authentication, populate <code>service_account_string</code> with the full JSON content of your service account key file</li>
                </ul>
              </div>
            )}
          </div>
        );
      default: // text
        const isSensitive = isSensitiveField(field.field_name);
        const isVisible = visibleSensitiveFields[field.field_name] || false;

        return (
          <div className="form-group">
            <label htmlFor={field.field_name}>
              {required ? `${field.field_label} *` : field.field_label}
            </label>
            <div className={`input-group ${isSensitive ? 'sensitive-field' : ''}`}>
              <input
                type={isSensitive && !isVisible ? "password" : "text"}
                id={field.field_name}
                className="form-control"
                value={value}
                onChange={(e) => handleChannelFieldChange(field.field_name, e.target.value)}
                required={required}
              />
              {isSensitive && (
                <div className="input-group-append">
                  <button
                    type="button"
                    className="btn btn-outline-secondary toggle-visibility-btn"
                    onClick={() => toggleSensitiveFieldVisibility(field.field_name)}
                    title={isVisible ? "Hide" : "Show"}
                  >
                    <FontAwesomeIcon icon={isVisible ? faEyeSlash : faEye} />
                  </button>
                </div>
              )}
            </div>
          </div>
        );
    }
  };

  // Channel Modal Component
  const ChannelModal = () => {
    const selectedType = channelTypes.find(type => type.id === selectedChannelType);

    return (
      <div className="modal-backdrop">
        <div className="modal">
          <div className="modal-header">
            <h3 className="modal-title">
              {editingChannel ? 'Edit Channel Config' : 'Add New Channel Config'}
            </h3>
            <button
              className="modal-close"
              onClick={() => setShowChannelModal(false)}
            >
              &times;
            </button>
          </div>
          <form onSubmit={handleChannelSubmit}>
            <div className="modal-body">
              {modalError && (
                <div className="alert alert-danger" style={{ marginBottom: '1rem' }}>
                  {modalError}
                </div>
              )}
              <div className="form-group">
                <label htmlFor="name">Channel Name <span className="text-danger">*</span></label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  className="form-control"
                  value={channelFormData.name}
                  onChange={handleChannelInputChange}
                  required

                />
              </div>

              <div className="form-group">
                <label htmlFor="channel_type_id">Channel Type <span className="text-danger">*</span></label>
                <select
                  id="channel_type_id"
                  name="channel_type_id"
                  className="form-control"
                  value={channelFormData.channel_type_id}
                  onChange={handleChannelTypeChange}
                  required
                  disabled={editingChannel} // Can't change channel type when editing
                >
                  <option value="">-- Select Channel Type --</option>
                  {channelTypes.map(type => (
                    <option key={type.id} value={type.id}>
                      {type.name}
                    </option>
                  ))}
                </select>
              </div>

              {selectedType && (
                <div className="channel-fields">
                  <h4>Channel Configuration</h4>
                  {getSortedFields(selectedType.fields, selectedType)
                    .filter(field => shouldShowField(field, selectedType))
                    .map(field => (
                      <div key={field.id}>
                        {renderChannelFieldInput(field, selectedType)}
                      </div>
                    ))}
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button type="submit" className="btn btn-success">
                <FontAwesomeIcon icon={editingChannel ? faEdit : faPlus} />
                {editingChannel ? 'Update Channel' : 'Create Channel'}
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setShowChannelModal(false)}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  // Account Channel Modal Component
  const AccountChannelModal = () => {
    return (
      <div className="modal-backdrop">
        <div className="modal">
          <div className="modal-header">
            <h3 className="modal-title">
              Assign Channel to Account
            </h3>
            <button
              className="modal-close"
              onClick={() => setShowAccountChannelModal(false)}
            >
              &times;
            </button>
          </div>
          <form onSubmit={handleAccountChannelSubmit}>
            <div className="modal-body">
              <div className="form-group">
                <label htmlFor="account_id">Account <span className="text-danger">*</span></label>
                <select
                  id="account_id"
                  name="account_id"
                  className="form-control"
                  value={accountChannelFormData.account_id}
                  onChange={handleAccountChannelInputChange}
                  required
                >
                  <option value="">-- Select Account --</option>
                  {accounts.map(account => (
                    <option key={account.id} value={account.id}>
                      {account.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="channel_id">Channel <span className="text-danger">*</span></label>
                <select
                  id="channel_id"
                  name="channel_id"
                  className="form-control"
                  value={accountChannelFormData.channel_id}
                  onChange={handleAccountChannelInputChange}
                  required
                >
                  <option value="">-- Select Channel --</option>
                  {channels.map(channel => (
                    <option key={channel.id} value={channel.id}>
                      {channel.name} ({channel.channel_type_name})
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-check">
                <input
                  type="checkbox"
                  id="is_enabled"
                  name="is_enabled"
                  className="form-check-input"
                  checked={accountChannelFormData.is_enabled}
                  onChange={handleAccountChannelInputChange}
                />
                <label className="form-check-label ml-2" htmlFor="is_enabled">
                  Enable Channel for this Account
                </label>
              </div>
            </div>

            <div className="modal-footer">
              <button type="submit" className="btn btn-success">
                <FontAwesomeIcon icon={faLink} /> Assign Channel
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setShowAccountChannelModal(false)}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="loading-container">
        <div className="loading-content">
          {/* <div className="loading-spinner"></div> */}
          <p>Loading channel configurations...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-card">
      <div className="admin-card-header">
        <h2 className="admin-card-title">
          <FontAwesomeIcon icon={faSatelliteDish} className="icon" />
          Channel Management
        </h2>
        <div className="admin-card-actions">
          <button
            className="btn btn-secondary mr-2"
            onClick={handleAddChannel}
          >
            <FontAwesomeIcon icon={faPlus} /> Add Channel Config
          </button>
          <button
            className="btn btn-secondary"
            onClick={handleAddAccountChannel}
          >
            <FontAwesomeIcon icon={faLink} /> Assign Channel
          </button>
        </div>
      </div>

      {error && (
        <div className="alert alert-danger">{error}</div>
      )}

      {/* Channels Section */}
      <div className="section-header mt-4 pt-3 border-top">
        <h3 className="admin-card-title">
          <FontAwesomeIcon icon={faSatelliteDish} className="icon" />
          Channel Configs
        </h3>
      </div>

      {channels.length === 0 ? (
        <div className="empty-state">
          <FontAwesomeIcon icon={faSatelliteDish} className="empty-state-icon" />
          <p className="empty-state-text">No channels defined yet</p>
          <button
            className="btn btn-secondary"
            onClick={handleAddChannel}
          >
            <FontAwesomeIcon icon={faPlus} /> Add First Channel Config
          </button>
        </div>
      ) : (
        <>
        <div className="search-bar mt-3">
          <input
            type="text"
            className="search-input"
            placeholder="Filter configs and assignments by channel name..."
            value={nameFilterInputValue}
            onChange={handleNameFilterChange}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                setNameFilter(nameFilterInputValue);
              }
            }}
          />
          <button
            className="search-button"
            onClick={() => setNameFilter(nameFilterInputValue)}
            title="Filter channel configs"
          >
            <FontAwesomeIcon icon={faSearch} />
          </button>
        </div>
        {sortedChannels.length === 0 ? (
          <div className="empty-state">
            <FontAwesomeIcon icon={faSatelliteDish} className="empty-state-icon" />
            <p className="empty-state-text">No channel configs match "{nameFilter}"</p>
          </div>
        ) : (
        <div className="table-responsive">
          <table className="admin-table">
            <thead>
              <tr className="bg-primary text-white" style={{ fontSize: '1.1em', fontWeight: 'bold' }}>
                <th onClick={() => requestSort('name')} className="sortable-header py-3">
                  Name{getSortDirectionIndicator('name')}
                </th>
                <th onClick={() => requestSort('channel_type_name')} className="sortable-header py-3">
                  Type{getSortDirectionIndicator('channel_type_name')}
                </th>
                <th onClick={() => requestSort('created_by_email')} className="sortable-header py-3">
                  Created By{getSortDirectionIndicator('created_by_email')}
                </th>
                <th onClick={() => requestSort('created_at')} className="sortable-header py-3">
                  Created On{getSortDirectionIndicator('created_at')}
                </th>
                <th className="py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedChannels.map(channel => (
                <tr key={channel.id}>
                  <td>{channel.name}</td>
                  <td>
                    <span className="badge badge-info" style={{ backgroundColor: '#00C0CE', color: 'white' }}>
                      {channel.channel_type_name}
                    </span>
                  </td>
                  <td>{channel.created_by_email || '-'}</td>
                  <td>{formatDate(channel.created_at)}</td>
                  <td>
                    <div className="action-buttons">
                      <button
                        className="btn btn-sm btn-secondary"
                        onClick={() => handleEditChannel(channel)}
                        title="Edit Channel"
                      >
                        <FontAwesomeIcon icon={faEdit} />
                      </button>
                      <button
                        className="btn btn-sm btn-secondary"
                        onClick={() => handleDuplicateChannel(channel)}
                        title="Duplicate Channel"
                      >
                        <FontAwesomeIcon icon={faCopy} />
                      </button>
                      <button
                        className="btn btn-sm btn-danger"
                        onClick={() => handleDeleteChannel(channel.id)}
                        title="Delete Channel"
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        )}
        </>
      )}

      {/* Account Channels Section */}
      <div className="section-header mt-5 pt-4 border-top">
        <h3 className="admin-card-title">
          <FontAwesomeIcon icon={faLink} className="icon" />
          Account Channel Assignments
        </h3>
      </div>

      {accountChannels.length === 0 ? (
        <div className="empty-state">
          <FontAwesomeIcon icon={faLink} className="empty-state-icon" />
          <p className="empty-state-text">No channel assignments yet</p>
          <button
            className="btn btn-secondary"
            onClick={handleAddAccountChannel}
          >
            <FontAwesomeIcon icon={faLink} /> Assign First Channel
          </button>
        </div>
      ) : sortedAccountChannels.length === 0 ? (
        <div className="empty-state">
          <FontAwesomeIcon icon={faLink} className="empty-state-icon" />
          <p className="empty-state-text">No channel assignments match "{nameFilter}"</p>
        </div>
      ) : (
        <div className="table-responsive">
          <table className="admin-table">
            <thead>
              <tr className="bg-primary text-white" style={{ fontSize: '1.1em', fontWeight: 'bold' }}>
                <th onClick={() => requestSort('account_name')} className="sortable-header py-3">
                  Account{getSortDirectionIndicator('account_name')}
                </th>
                <th onClick={() => requestSort('channel_name')} className="sortable-header py-3">
                  Channel{getSortDirectionIndicator('channel_name')}
                </th>
                <th onClick={() => requestSort('channel_type_name')} className="sortable-header py-3">
                  Type{getSortDirectionIndicator('channel_type_name')}
                </th>
                <th onClick={() => requestSort('is_enabled')} className="sortable-header py-3">
                  Status{getSortDirectionIndicator('is_enabled')}
                </th>
                <th onClick={() => requestSort('created_at')} className="sortable-header py-3">
                  Assigned On{getSortDirectionIndicator('created_at')}
                </th>
                <th className="py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedAccountChannels.map(ac => (
                <tr key={ac.id}>
                  <td>{ac.account_name}</td>
                  <td>{ac.channel_name}</td>
                  <td>
                    <span className="badge badge-info" style={{ backgroundColor: '#00C0CE', color: 'white' }}>
                      {ac.channel_type_name}
                    </span>
                  </td>
                  <td>
                    <span className={`badge badge-${ac.is_enabled ? 'success' : 'secondary'}`}>
                      {ac.is_enabled ? 'Enabled' : 'Disabled'}
                    </span>
                  </td>
                  <td>{formatDate(ac.created_at)}</td>
                  <td>
                    <div className="action-buttons">
                      <button
                        className={`btn btn-sm ${ac.is_enabled ? 'btn-success' : 'btn-secondary'}`}
                        onClick={() => handleToggleAccountChannel(ac.id, ac.is_enabled)}
                        title={ac.is_enabled ? 'Disable Channel' : 'Enable Channel'}
                      >
                        <FontAwesomeIcon icon={ac.is_enabled ? faToggleOn : faToggleOff} />
                      </button>
                      <button
                        className="btn btn-sm btn-danger"
                        onClick={() => handleRemoveAccountChannel(ac.id)}
                        title="Remove Channel Assignment"
                      >
                        <FontAwesomeIcon icon={faUnlink} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modals */}
      {showChannelModal && <ChannelModal />}
      {showAccountChannelModal && <AccountChannelModal />}
    </div>
  );
}

export default ChannelManagement;
